/**
 * Shared domain types for the Synthec web platform.
 *
 * Locale == the first URL segment ("peru" | "colombia"), kept as the literal
 * Spanish word so the rebuilt URLs match the current WordPress paths exactly.
 * Country == the two-letter market code used inside Payload (pe | co).
 */

export const LOCALES = ['peru', 'colombia'] as const
export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'peru'

export type CountryCode = 'pe' | 'co'

export const localeToCountry: Record<Locale, CountryCode> = {
  peru: 'pe',
  colombia: 'co',
}

export const countryToLocale: Record<CountryCode, Locale> = {
  pe: 'peru',
  co: 'colombia',
}

export const localeLabel: Record<Locale, string> = {
  peru: 'Perú',
  colombia: 'Colombia',
}

/** `es-PE` / `es-CO` — for <html lang> and hreflang. */
export const localeHtmlLang: Record<Locale, string> = {
  peru: 'es-PE',
  colombia: 'es-CO',
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value)
}

// --- Content DTOs (shape the frontend consumes from Payload; loose for now) ---

export interface SeoMeta {
  title?: string
  description?: string
  canonical?: string
  noindex?: boolean
  ogImage?: string
}

export interface MediaRef {
  url: string
  alt?: string
  width?: number
  height?: number
}

export interface BrandSummary {
  slug: string
  name: string
  logo?: MediaRef
}

export interface CategoryRef {
  slug: string
  title: string
  /** Full path segments, e.g. ["lubricantes-industriales", "lubricantes-de-alto-desempeno"]. */
  path: string[]
}

export interface ProductSummary {
  slug: string
  title: string
  sku?: string
  brand?: BrandSummary
  image?: MediaRef
  categories?: CategoryRef[]
}
